import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Bot, TrendingUp, AlertTriangle, Newspaper, Lightbulb, X } from 'lucide-react';
import { useT } from '../i18n';

/* IntelligenceBar — LIVE RESTAURANT INTELLIGENCE.
   Slim graphite marquee above the header. Curated items + Google News from
   /api/intelligence-feed. Kind icons, editorial level badges (BREAKING/HIGH/TIP),
   "Updated Xm ago" credibility stamp, label links to /intelligence. */

const API = process.env.REACT_APP_BACKEND_URL;

export function trackIntelClick(id) {
  try {
    fetch(`${API}/api/intelligence-feed/${id}/click`, { method: 'POST', keepalive: true }).catch(() => {});
  } catch {
    /* engagement tracking must never break navigation */
  }
}

const KIND_ICONS = {
  ai: { Icon: Bot, cls: 'text-[#FF7A00]' },
  revenue: { Icon: TrendingUp, cls: 'text-emerald-400' },
  alert: { Icon: AlertTriangle, cls: 'text-red-400' },
  news: { Icon: Newspaper, cls: 'text-sky-400' },
  insight: { Icon: Lightbulb, cls: 'text-amber-300' },
};

const LEVELS = {
  breaking: { en: 'BREAKING', es: 'ÚLTIMA HORA', cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
  high: { en: 'HIGH', es: 'ALTA', cls: 'bg-[#FF7A00]/15 text-[#FF9B4A] border-[#FF7A00]/30' },
  tip: { en: 'TIP', es: 'TIP', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' },
};

function ItemLink({ item, es, onExternal }) {
  const label = (es && item.title_es) || item.title;
  const { Icon, cls } = KIND_ICONS[item.kind] || KIND_ICONS.insight;
  const lvl = item.source === 'news' ? null : LEVELS[item.level];
  const inner = (
    <>
      <Icon className={`w-3 h-3 shrink-0 ${cls}`} aria-hidden="true" />
      {lvl && (
        <span className={`shrink-0 rounded-[3px] border px-1.5 py-0.5 text-[10.5px] font-bold tracking-[0.08em] leading-none ${lvl.cls}`}>
          {es ? lvl.es : lvl.en}
        </span>
      )}
      <span className="truncate max-w-[420px]">{label}</span>
      {item.cta && <span className="text-[#FF7A00] font-semibold shrink-0 group-hover/intel:underline underline-offset-2">{item.cta} →</span>}
    </>
  );
  const clsA =
    `group/intel inline-flex items-center gap-1.5 text-[14.5px] text-[#F8F6F2]/95 hover:text-white transition-colors duration-200 whitespace-nowrap${item.level === 'breaking' ? ' kx-breaking' : ''}`;
  if (/^https?:\/\//.test(item.url)) {
    return (
      <a href={`/go?u=${encodeURIComponent(item.url)}`} target="_blank" rel="noopener noreferrer" className={clsA}
        onClick={() => { trackIntelClick(item.id); onExternal?.(); }} data-testid={`intel-item-${item.id}`}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={item.url} className={clsA} onClick={() => trackIntelClick(item.id)} data-testid={`intel-item-${item.id}`}>
      {inner}
    </Link>
  );
}

export default function IntelligenceBar() {
  const [items, setItems] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [paused, setPaused] = useState(() => localStorage.getItem('kx_ticker_paused') === '1');
  const [nudge, setNudge] = useState(false);
  const onExternal = () => {
    if (sessionStorage.getItem('kx_affil_nudge_dismissed')) return;
    setNudge(true);
  };
  const dismissNudge = () => {
    sessionStorage.setItem('kx_affil_nudge_dismissed', '1');
    setNudge(false);
  };
  useEffect(() => { localStorage.setItem('kx_ticker_paused', paused ? '1' : '0'); }, [paused]);
  const [, tick] = useState(0);
  const { lang } = useT();
  const es = lang === 'es';

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/api/intelligence-feed?limit=34`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (alive && d?.items) {
            setItems(d.items);
            setFetchedAt(Date.now());
          }
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    const tk = setInterval(() => tick((n) => n + 1), 30 * 1000);
    return () => { alive = false; clearInterval(t); clearInterval(tk); };
  }, []);

  if (!items.length) {
    // Reserve the bar's exact height from first paint — the site must never
    // shift down when the feed loads (CLS fix: logos/icons stay centered).
    return (
      <div className="bg-[#1B1F24] h-12 sm:h-11 border-t border-white/[0.08] flex items-center overflow-hidden" data-testid="intelligence-bar" aria-hidden="true">
        <span className="flex items-center gap-2 pl-4 sm:pl-6">
          <span className="relative flex w-1.5 h-1.5">
            <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400/60" />
          </span>
          <span className="text-[11.5px] sm:text-[12px] tracking-[0.22em] uppercase font-bold text-white/70 whitespace-nowrap">Kitxens Now</span>
        </span>
      </div>
    );
  }
  const visible = items.filter((it) => !it.lang || it.lang === lang);
  const promo = {
    id: 'kx-partner-promo', kind: 'revenue', level: 'tip',
    title: 'Restaurant friends? Refer Kitxens and earn 30% recurring',
    title_es: '¿Amigos con restaurante? Recomienda Kitxens y gana 30% recurrente',
    cta: es ? 'Hazte partner' : 'Become a partner', url: '/affiliates',
  };
  const feed = visible.length > 3 ? [...visible.slice(0, 3), promo, ...visible.slice(3)] : [...visible, promo];
  const duration = Math.max(55, feed.length * 8);
  const mins = fetchedAt ? Math.floor((Date.now() - fetchedAt) / 60000) : 0;
  const updatedLabel = es
    ? (mins < 1 ? 'ahora' : `hace ${mins} min`)
    : (mins < 1 ? 'just now' : `${mins} min ago`);

  return (
    <div className="bg-[#1B1F24] h-12 sm:h-11 border-t border-white/[0.08] flex items-stretch overflow-hidden" data-testid="intelligence-bar">
      {/* Persistent bottom retention strip — appears after leaving to an external article */}
      {nudge && createPortal(
        <div className="fixed bottom-0 inset-x-0 z-[75] bg-[#16181D] border-t-2 border-[#F77F11] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.6)]" data-testid="affiliate-nudge" role="status">
          <div className="max-w-[1180px] mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-5">
            <p className="flex-1 text-[13px] sm:text-[13.5px] text-neutral-300 leading-snug min-w-0">
              <span className="font-bold text-white">kit<span className="text-[#F77F11]">x</span>ens</span>{' · '}
              {es
                ? 'Cada tendencia que leas, te ayudamos a aplicarla en tu restaurante — y si la compartes, puedes cobrar por ello.'
                : 'Every trend you read, we help you apply it to your restaurant — and if you share it, you can get paid for it.'}
            </p>
            <div className="flex items-center gap-2.5 shrink-0">
              <Link to="/index-ai#scan" onClick={dismissNudge}
                className="inline-flex items-center gap-1.5 bg-[#F77F11] hover:bg-[#d8651a] text-white h-9 px-4 rounded-full text-[12.5px] font-bold transition-colors whitespace-nowrap" data-testid="affiliate-nudge-scan">
                {es ? 'Escaneo gratis (30s)' : 'Free scan (30s)'}
              </Link>
              <Link to="/affiliates" onClick={dismissNudge}
                className="inline-flex items-center gap-1.5 border border-white/25 hover:border-[#F77F11] text-white hover:text-[#FF9B4A] h-9 px-4 rounded-full text-[12.5px] font-semibold transition-colors whitespace-nowrap" data-testid="affiliate-nudge-cta">
                {es ? 'Gana 30% como partner' : 'Earn 30% as a partner'}
              </Link>
              <button type="button" onClick={dismissNudge} aria-label={es ? 'Cerrar' : 'Close'}
                className="shrink-0 text-neutral-500 hover:text-white transition-colors p-1" data-testid="affiliate-nudge-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* Fixed label — links to the public intelligence hub */}
      <Link
        to="/intelligence"
        className="group relative z-10 shrink-0 flex items-center gap-2 pl-4 sm:pl-6 pr-4 bg-[#1B1F24] hover:bg-[#232830] transition-colors"
        data-testid="intelligence-bar-link"
        title={es ? 'Ver todo el Intelligence Hub' : 'See the full Intelligence Hub'}
      >
        <span className="relative flex w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
          <span className="relative w-1.5 h-1.5 rounded-full bg-emerald-400" />
        </span>
        <span className="text-[11.5px] sm:text-[12px] tracking-[0.22em] uppercase font-bold text-white whitespace-nowrap group-hover:text-[#FF9B4A] transition-colors">
          Kitxens Now
        </span>
        <span className="hidden md:inline text-[10px] text-neutral-400 whitespace-nowrap" data-testid="intelligence-bar-updated">
          · {updatedLabel}
        </span>
      </Link>

      {/* Marquee track */}
      <div className="relative flex-1 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]">
        <div
          className="kx-intel-track absolute inset-y-0 flex items-center gap-5 pr-5 hover:![animation-play-state:paused]"
          style={{ animation: `kx-intel-scroll ${duration}s linear infinite`, animationPlayState: paused ? 'paused' : 'running' }}
        >
          {[...feed, ...feed].map((item, i) => (
            <React.Fragment key={`${item.id}-${i}`}>
              <span className="text-[#FF7A00]/70 text-[7px] shrink-0 select-none" aria-hidden="true">▶</span>
              <ItemLink item={item} es={es} onExternal={onExternal} />
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* WCAG pause/play control */}
      <button
        type="button"
        onClick={() => setPaused((p) => !p)}
        aria-pressed={paused}
        aria-label={paused ? (es ? 'Reanudar el ticker de noticias' : 'Resume news ticker') : (es ? 'Pausar el ticker de noticias' : 'Pause news ticker')}
        title={paused ? (es ? 'Reanudar' : 'Resume') : (es ? 'Pausar' : 'Pause')}
        className="relative z-10 shrink-0 flex items-center justify-center w-9 bg-[#1B1F24] hover:bg-[#232830] text-neutral-400 hover:text-white transition-colors border-l border-white/[0.07]"
        data-testid="intelligence-bar-pause"
      >
        {paused
          ? <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
          : <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>}
      </button>
      <style>{`
        @keyframes kx-intel-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .kx-intel-track { min-width: max-content; will-change: transform; }
        .kx-breaking { border-radius: 6px; padding: 2px 7px; margin: -2px -3px; }
        .kx-breaking:hover {
          animation: kx-flash 0.9s ease-out forwards;
        }
        @keyframes kx-flash {
          0% { background-color: rgba(255,122,0,0.5); box-shadow: 0 0 18px rgba(255,122,0,0.45); }
          100% { background-color: rgba(255,122,0,0.14); box-shadow: 0 0 0 rgba(255,122,0,0); }
        }
      `}</style>
    </div>
  );
}
